import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Button, Modal } from "react-bootstrap";
import PageLoading from "components/PageLoading";
import "./ExtractAnalysisV2.css";

import ExtratoConteudoV2 from "../ComponentsExtractAnalysis/ExtratoConteudoV2/ExtratoConteudoV2";
import ModalConfirmEnvioExtrato from "../ComponentsExtractAnalysis/ModalConfirmEnvioExtrato/ModalConfirmEnvioExtrato";
import ModalExtractVisualizacaoV2 from "../ComponentsExtractAnalysis/ModalExtractVisualizacaoV2/ModalExtractVisualizacaoV2";
import NotificationToast from "../../../../components/NotificationToast/NotificationToast";

import {
  salvarExtratoAnalise,
  enviarExtratoSolicitacaoV2,
  getExtratoSolicitacaoById,
  postExtratoRetornoSolicitacao,
  type ExtratoValidacaoSindicato,
} from "../../../../services/ExtractService";

import {
  mapExtratoSolicitacaoApiToDeducoes,
  mapExtratoSolicitacaoApiToPreview,
  mapExtratoSolicitacaoApiToValoresSolicitados,
  type ValoresSolicitadosView,
} from "../V2/mapExtratoSolicitacaoApi";
import { buildPatchExtratoAnalisePayload } from "../V2/buildPatchExtratoAnalisePayload";
import { gerarPdfExtratoBlob } from "../V2/gerarPdfExtrato";

import {
  type DadosDeducao,
  MSG_OUTRAS_DEDUCOES_EXCEDEM_BRUTO,
  MSG_OUTRAS_DEDUCOES_INVALIDAS,
  outrasDeducoesExcedemBruto,
  validarOutrasDeducoes,
} from "../ComponentsExtractAnalysis/DeducaoTable/DeducaoTable";
import { PATHS } from "../../../../routes/paths";

interface DadosSindicato {
  nome?: string;
  cnpj?: string;
  sigla?: string;
}

interface PreviewExtrato {
  valorMinimo?: number;
  valorBonus?: number;
  valorBonusExtra?: number;
  valorTotalApoioBruto?: number;
  receitaOperacional?: number;
  faixaReceitaBonus?: {
    titulo?: string;
    receitaMin?: number;
    receitaMax?: number;
    valorMinimo?: number;
    valorBonusPotencial?: number;
    valorMaximoPotencial?: number;
  };
  criterioBonus?: Array<{
    documento?: string;
    nome?: string;
    conforme?: boolean;
    percentual?: number;
    valor?: string | number;
  }>;
  criterioBonusExtra?: Array<{
    documento?: string;
    nome?: string;
    conforme?: boolean;
    percentual?: number;
    valor?: string | number;
  }>;
}



const ExtractAnalysisV2 = () => {
  const { idResposta: idQuestionario } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = (location.state ?? {}) as {
    selectedYear?: number;
    solicitacao?: string;
  };
  const selectedYear = routeState.selectedYear ?? null;

  const [dadosSindicato, setDadosSindicato] = useState<DadosSindicato>({});
  const [valoresSolicitados, setValoresSolicitados] =
    useState<ValoresSolicitadosView | null>(null);
  const [solicitacaoLabel, setSolicitacaoLabel] = useState(
    routeState.solicitacao ?? "",
  );
  const [previewData, setPreviewData] = useState<PreviewExtrato | null>(null);
  const [deducaoData, setDeducaoData] = useState<DadosDeducao | null>(null);
  const deducaoDataRef = useRef<DadosDeducao | null>(null);
  const [mostrarErrosOutras, setMostrarErrosOutras] = useState(false);
  const fetchDoneRef = useRef(false);

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [statusNome, setStatusNome] = useState<string | null>(null);
  const [valorLiquidoApoioApi, setValorLiquidoApoioApi] = useState<number | null>(null);
  const [validacaoSindicato, setValidacaoSindicato] = useState<ExtratoValidacaoSindicato | null>(null);

  const [showModalVisualizacao, setShowModalVisualizacao] = useState(false);
  const [showModalConfirm, setShowModalConfirm] = useState(false);
  const [showModalRetomar, setShowModalRetomar] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const [toastData, setToastData] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const pdfContentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!idQuestionario) return;
    if (fetchDoneRef.current) return;
    fetchDoneRef.current = true;

    const fetchDados = async () => {
      setCarregando(true);
      try {
        const detalhe = await getExtratoSolicitacaoById(idQuestionario);
        if (!detalhe) throw new Error("Extrato não encontrado.");

        setDadosSindicato({
          nome: detalhe.dadosSindicato?.nome ?? "",
          cnpj: detalhe.dadosSindicato?.cnpj ?? "",
          sigla: detalhe.dadosSindicato?.sigla ?? "",
        });
        setPreviewData(mapExtratoSolicitacaoApiToPreview(detalhe));
        const deducoes = mapExtratoSolicitacaoApiToDeducoes(detalhe);
        setDeducaoData(deducoes);
        deducaoDataRef.current = deducoes;
        setValoresSolicitados(
          mapExtratoSolicitacaoApiToValoresSolicitados(detalhe),
        );
        setStatusNome((detalhe as any).status?.nome ?? null);
        setValorLiquidoApoioApi(detalhe.valorLiquidoApoio ?? null);
        setValidacaoSindicato(detalhe.validacaoSindicato ?? null);
        if (
          !solicitacaoLabel &&
          detalhe.valoresJaSolicitadosExtrato?.solicitacoes?.[0]
        ) {
          setSolicitacaoLabel(
            detalhe.valoresJaSolicitadosExtrato.solicitacoes[0].solicitacao ??
              "1ª Solicitação",
          );
        }
      } catch (e) {
        console.error("Erro ao carregar extrato V2:", e);
        setErro("Erro ao carregar dados do extrato.");
      } finally {
        setCarregando(false);
      }
    };

    fetchDados();
  }, [idQuestionario]);

  const atualizarDeducoes = useCallback((dados: DadosDeducao) => {
    setDeducaoData(dados);
    deducaoDataRef.current = dados;
  }, []);

  const handleSalvar = async () => {
    // Valida antes do loading: a tabela fica montada e mostra os campos em vermelho.
    const outrasInvalidas = validarOutrasDeducoes(
      deducaoDataRef.current?.deducaoOutros,
    );
    if (Object.keys(outrasInvalidas).length > 0) {
      setMostrarErrosOutras(true);
      return {
        show: true,
        message: MSG_OUTRAS_DEDUCOES_INVALIDAS,
        type: "danger",
      };
    }

    if (
      outrasDeducoesExcedemBruto(
        deducaoDataRef.current?.deducaoOutros,
        previewData?.valorTotalApoioBruto,
      )
    ) {
      return {
        show: true,
        message: MSG_OUTRAS_DEDUCOES_EXCEDEM_BRUTO,
        type: "danger",
      };
    }

    setCarregando(true);
    try {
      const dadosParaSalvar = deducaoDataRef.current;

      if (!dadosParaSalvar || !idQuestionario) {
        return {
          show: true,
          message: "Dados incompletos para salvar o extrato.",
          type: "danger",
        };
      }

      const payload = buildPatchExtratoAnalisePayload(dadosParaSalvar);

      const sucesso = await salvarExtratoAnalise(idQuestionario, payload);

      if (sucesso) {
        const detalhe = await getExtratoSolicitacaoById(idQuestionario);
        if (detalhe) {
          const novasDeducoes = mapExtratoSolicitacaoApiToDeducoes(detalhe);
          setDeducaoData(novasDeducoes);
          deducaoDataRef.current = novasDeducoes;
          setPreviewData(mapExtratoSolicitacaoApiToPreview(detalhe));
          setValoresSolicitados(
            mapExtratoSolicitacaoApiToValoresSolicitados(detalhe),
          );
          setStatusNome((detalhe as any).status?.nome ?? null);
          setValorLiquidoApoioApi(detalhe.valorLiquidoApoio ?? null);
          setValidacaoSindicato(detalhe.validacaoSindicato ?? null);
        }
      }

      return {
        show: true,
        message: sucesso
          ? "Extrato salvo com sucesso."
          : "Erro ao salvar extrato.",
        type: sucesso ? "success" : "danger",
      };
    } catch {
      return {
        show: true,
        message: "Erro inesperado ao salvar o extrato.",
        type: "danger",
      };
    } finally {
      setCarregando(false);
    }
  };

  const handleClickSalvar = async () => {
    const result = await handleSalvar();
    if (result?.show) {
      setToastData({
        show: true,
        message: result.message ?? "",
        type: result.type ?? "success",
      });
    }
  };


  const handleGerar = async () => {
    const result = await handleSalvar();
    if (result?.type === "success") {
      setShowModalVisualizacao(true);
    } else if (result?.show) {
      setToastData({
        show: true,
        message: result.message ?? "Erro ao salvar.",
        type: result.type ?? "danger",
      });
    }
  };

  const buildPdfClone = (source: HTMLElement): { clone: HTMLElement; cleanup: () => void } => {
    const clone = source.cloneNode(true) as HTMLElement;
    // Usa o maior entre o offsetWidth real e 1200px para garantir que as colunas das tabelas caibam sem overflow
    const pdfWidth = Math.max(source.offsetWidth, 1200);
    clone.style.cssText = `position:fixed;left:-9999px;top:0;width:${pdfWidth}px;background:#fff;`;
    clone.classList.add("v2-pdf-generating");
    // Oculta linhas não deduzidas e colunas DEDUZIR/AÇÃO
    clone.querySelectorAll<HTMLElement>("[data-pdf-filter='false']").forEach((row) => {
      row.style.display = "none";
    });
    clone.querySelectorAll<HTMLElement>(".deduzir-header,.deduzir-cell,.acao-header,.acao-cell,.adicionar-linha-btn").forEach((el) => {
      el.style.display = "none";
    });
    document.body.appendChild(clone);
    return { clone, cleanup: () => document.body.removeChild(clone) };
  };

  const handleRetomar = () => {
    setShowModalRetomar(true);
  };

  const handleConfirmarRetomar = async () => {
    if (!idQuestionario) return;
    setShowModalRetomar(false);
    try {
      await postExtratoRetornoSolicitacao(idQuestionario);
      setToastData({ show: true, message: "Análise retomada com sucesso.", type: "success" });
      setTimeout(() => navigate(PATHS.DOC_EXTRACT), 1500);
    } catch {
      setToastData({
        show: true,
        message: "Erro ao retomar análise.",
        type: "danger",
      });
    }
  };

  const handleEnviar = async () => {
    setEnviando(true);
    const source = pdfContentRef.current;
    if (!source) return;
    const { clone, cleanup } = buildPdfClone(source);
    try {
      const blob = await gerarPdfExtratoBlob(clone);

      const sucesso = await enviarExtratoSolicitacaoV2(idQuestionario!, blob);

      if (sucesso) setStatusNome("ExtratoEnviado");
      setToastData({
        show: true,
        message: sucesso
          ? "Extrato enviado com sucesso!"
          : "Falha no envio do extrato.",
        type: sucesso ? "success" : "warning",
      });
      setShowModalConfirm(false);
    } catch {
      setToastData({
        show: true,
        message: "Erro ao gerar ou enviar o PDF.",
        type: "danger",
      });
    } finally {
      cleanup();
      setEnviando(false);
    }
  };

  if (carregando) return <PageLoading />;
  if (erro) return <p className="text-danger">{erro}</p>;

  const statusKey = (statusNome ?? "").toLowerCase().replace(/\s+/g, "");
  const isEnviado  = statusKey === "extratoenviado";
  const isPendente = statusKey === "extratopendente";
  const isInapto   = statusKey === "inapto";
  const isRecusado = statusKey === "extratorecusado";

  // Pendente / Recusado → pode editar e salvar (Inapto: somente leitura)
  const podeEditar  = isPendente || isRecusado;
  // Pendente / Recusado / Inapto → pode gerar extrato
  const podeGerar   = isPendente || isRecusado || isInapto;
  // Todos exceto Validado → pode retomar análise
  const podeRetomar = isPendente || isEnviado || isRecusado || isInapto;

  return (
    <Container className="v2-extract-container py-4">
      <div ref={pdfContentRef}>
        <ExtratoConteudoV2
          showPageHeader={true}
          onNavigateBack={() => navigate(PATHS.DOC_EXTRACT)}
          solicitacaoLabel={solicitacaoLabel}
          selectedYear={selectedYear}
          dadosSindicato={dadosSindicato}
          validacaoSindicato={validacaoSindicato}
          previewData={previewData}
          deducaoData={deducaoData}
          valoresSolicitados={valoresSolicitados}
          valorLiquidoApoioApi={valorLiquidoApoioApi}
          onUpdateDeducoes={atualizarDeducoes}
          mostrarErrosOutras={mostrarErrosOutras}
          disabled={!podeEditar}
          isInapto={isInapto}
        />
      </div>

      {/* Ações */}
      <div className="v2-actions-footer">
        <div className="save-submit-buttons d-flex gap-2">
          {podeRetomar && (
            <Button
              variant="outline-secondary"
              className="v2-btn-retomar"
              onClick={handleRetomar}
            >
              RETOMAR ANÁLISE
            </Button>
          )}

          {podeEditar && (
            <Button
              variant="primary"
              className="save-btn"
              onClick={handleClickSalvar}
            >
              SALVAR
            </Button>
          )}

          {podeGerar && (
            <Button
              variant="success"
              className="sucess-btn"
              onClick={handleGerar}
            >
              GERAR EXTRATO
            </Button>
          )}
        </div>
      </div>

      <ModalExtractVisualizacaoV2
        show={showModalVisualizacao}
        onClose={() => setShowModalVisualizacao(false)}
        onSolicitarConfirmacaoEnvio={() => {
          setShowModalVisualizacao(false);
          setTimeout(() => setShowModalConfirm(true), 200);
        }}
        dadosSindicato={dadosSindicato}
        previewData={previewData}
        deducaoData={deducaoData}
        valorLiquidoApoioApi={valorLiquidoApoioApi}
        solicitacaoLabel={solicitacaoLabel}
        selectedYear={selectedYear}
      />

      <ModalConfirmEnvioExtrato
        show={showModalConfirm}
        onClose={() => {
          setShowModalConfirm(false);
          setTimeout(() => setShowModalVisualizacao(true), 200);
        }}
        onConfirm={handleEnviar}
        enviando={enviando}
      />

      <Modal
        show={showModalRetomar}
        onHide={() => setShowModalRetomar(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Retomar análise</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Deseja realmente retomar a análise documental desta solicitação?
        </Modal.Body>
        <Modal.Footer className="justify-content-center gap-3">
          <Button
            onClick={() => setShowModalRetomar(false)}
            style={{
              minWidth: "140px",
              background: "white",
              border: "1px solid #005ca9",
              color: "#005ca9",
            }}
          >
            NÃO
          </Button>
          <Button
            onClick={handleConfirmarRetomar}
            style={{
              minWidth: "140px",
              background: "#005ca9",
              border: "1px solid #005ca9",
              color: "white",
            }}
          >
            SIM
          </Button>
        </Modal.Footer>
      </Modal>

      <NotificationToast
        show={toastData.show}
        onClose={() => setToastData((prev) => ({ ...prev, show: false }))}
        message={toastData.message}
        type={toastData.type}
      />
    </Container>
  );
};

export default ExtractAnalysisV2;
